'use client'

import { useEffect } from 'react'
import { useStore } from '@/store'
import { tracking } from '@/tracking'
import { getCurrencyCodeForHost } from '@/lib/seo/marketUrls'
import { usePathname, useSearchParams } from 'next/navigation'

const CUSTOMER_IO_CONTEXT_STORAGE_KEY = 'vi:customer-io:context'

function getCustomerIoContextTraits() {
  const traits: Record<string, string> = {
    currency: getCurrencyCodeForHost(window.location.host).toLowerCase(),
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  if (timeZone) {
    traits.cio_timezone = timeZone
  }

  return traits
}

function shouldSendCustomerIoContext(
  customerId: string | number,
  traits: Record<string, string>
) {
  const key = `${CUSTOMER_IO_CONTEXT_STORAGE_KEY}:${customerId}`
  const signature = JSON.stringify(traits)

  try {
    if (window.localStorage.getItem(key) === signature) {
      return false
    }

    window.localStorage.setItem(key, signature)
    return true
  } catch {
    return true
  }
}

export const PageTracker = () => {
  const { user } = useStore()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const search = searchParams.toString()
  useEffect(() => {
    if (user?.email) {
      tracking.identify(user.email, { customerId: user.id })
      const customerIoContextTraits = getCustomerIoContextTraits()
      if (shouldSendCustomerIoContext(user.id, customerIoContextTraits)) {
        tracking.identify(
          user.email,
          { customerId: user.id, ...customerIoContextTraits },
          { destinations: ['customerIo'] }
        )
      }
    }
  }, [user])

  useEffect(() => {
    if (!pathname) return

    const searchWithPrefix = search ? `?${search}` : ''
    tracking.page(`${pathname}${searchWithPrefix}`, {
      path: pathname,
      search: searchWithPrefix,
      url: window.location.href,
      title: document.title,
    })
  }, [pathname, search])

  return null
}
